import { useRequiredContext } from "@redotech/react-util/context";
import {
  getCarrierDisplayName,
  getCarrierTrackingUrl,
} from "@redotech/redo-model/order-tracking/carriers/carrier-resolver";
import type { TrackingPageShipment } from "@redotech/redo-model/order-tracking/tracking-page-order-definition";
import classNames from "classnames";
import { memo } from "react";
import { Flex } from "../../../flex";
import { ExternalLink } from "../../../link";
import { replaceWordsByMerchantRequest } from "../../../settings-elements/order/order-details";
import { Text } from "../../../text";
import { TrackingPageContext } from "../../context/tracking-page-context";
import {
  getBodyStyles,
  getHeaderStyles,
  getTrackingNumberUrlStyles,
} from "../shared/block-styles";
import * as trackingCss from "./tracking-details.module.css";

export const TrackingCarrierInfo = memo(function TrackingCarrierInfo({
  shipment,
}: {
  shipment?: TrackingPageShipment;
}) {
  const { returnAppSettings, pageSettings } =
    useRequiredContext(TrackingPageContext);

  const trackingNumberUrlStyles = getTrackingNumberUrlStyles(pageSettings);
  const headerStyles = getHeaderStyles(pageSettings);
  const bodyStyles = getBodyStyles(pageSettings);

  const trackingCode = shipment?.trackingCode;

  const trackingUrl =
    getCarrierTrackingUrl(
      shipment?.carrier ?? undefined,
      trackingCode ?? undefined,
    ) ?? shipment?.trackingUrl;

  const hasTrackingUrl =
    !!trackingUrl && returnAppSettings.storeUrl !== "shopconzuri.myshopify.com";

  return (
    <Flex>
      {trackingCode && (
        <Flex dir="column" flex={1} gap="xs">
          <Text
            className={trackingCss.trackingTitle}
            fontSize="xl"
            style={headerStyles}
          >
            Tracking
          </Text>
          {hasTrackingUrl && trackingUrl ? (
            <ExternalLink
              className={classNames(trackingCss.tinyText)}
              style={trackingNumberUrlStyles}
              url={trackingUrl}
            >
              <Text
                fontWeight="semibold"
                style={trackingNumberUrlStyles}
                textColor="secondary"
              >
                {trackingCode}
              </Text>
            </ExternalLink>
          ) : (
            <Text
              className={classNames(trackingCss.tinyText)}
              fontWeight="semibold"
              style={bodyStyles}
              textColor="secondary"
            >
              {trackingCode}
            </Text>
          )}
        </Flex>
      )}
      {shipment?.carrier && (
        <Flex dir="column" flex={1} gap="xs">
          <Text fontSize="xl" style={headerStyles}>
            Carrier
          </Text>
          <Text
            className={classNames(trackingCss.tinyText)}
            fontWeight="semibold"
            style={bodyStyles}
          >
            {replaceWordsByMerchantRequest(
              getCarrierDisplayName(shipment.carrier),
              returnAppSettings.storeUrl,
            )}
          </Text>
        </Flex>
      )}
    </Flex>
  );
});
